
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ProfileFormData {
  username: string;
  displayName: string;
  email: string;
  phoneNumber: string;
  usernameUpdateAllowed: boolean;
  daysUntilUsernameChange: number;
  linkOne: string;
  linkTwo: string;
}

interface ProfileTabProps {
  formData: ProfileFormData;
  setFormData: (data: ProfileFormData) => void;
  isMobile: boolean;
}

export function ProfileTab({ formData, setFormData, isMobile }: ProfileTabProps) {
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isChangingUsername, setIsChangingUsername] = useState(false);
  const [isChangingDisplayName, setIsChangingDisplayName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingProfile(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      // Validate character limits
      if (formData.username.length > 25) {
        throw new Error(`${t('username')} ${t('must_not_exceed')} 25 ${t('characters')}`);
      }
      
      if (formData.displayName.length > 25) {
        throw new Error(`${t('display_name')} ${t('must_not_exceed')} 25 ${t('characters')}`);
      }
      
      // Check if username is being changed
      if (user.user_metadata?.username !== formData.username) {
        if (!formData.usernameUpdateAllowed) {
          throw new Error(`${t('username_cannot_change')} ${formData.daysUntilUsernameChange} ${t('more_days')}`);
        }
      }

      console.log("Updating user profile:", formData);

      const { error: authError } = await supabase.auth.updateUser({
        data: {
          username: formData.username,
          display_name: formData.displayName,
          username_updated_at: new Date().toISOString()
        },
        phone: formData.phoneNumber || undefined
      });
      
      if (authError) {
        console.error("Auth update error:", authError);
        throw authError;
      }

      const { error: profileError } = await supabase.from('profiles').update({
        username: formData.username,
        display_name: formData.displayName,
        link_one: formData.linkOne,
        link_two: formData.linkTwo
      }).eq('id', user.id);
      
      if (profileError) {
        console.error("Profile update error:", profileError);
        throw profileError;
      }
      
      toast.success(t("profile_updated_success") || "Profile updated successfully");
      setIsChangingUsername(false);
      setIsChangingDisplayName(false);
    } catch (error: any) {
      console.error("Profile update failed:", error);
      setError(error.message);
      toast.error(error.message);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  return (
    <Card>
      <CardHeader className="text-left">
        <CardTitle>{t('profile_settings')}</CardTitle>
      </CardHeader>
      <CardContent className="text-left">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleProfileUpdate} className="space-y-6 text-left">
          <div className="space-y-2">
            <Label htmlFor="displayName">
              {t('display_name')} <span className="text-muted-foreground text-xs">(max 25 characters)</span>
            </Label>
            {isChangingDisplayName ? (
              <div className="space-y-2">
                <Input 
                  id="displayName" 
                  value={formData.displayName} 
                  onChange={e => setFormData({ ...formData, displayName: e.target.value.slice(0, 25) })} 
                  placeholder={t('display_name_placeholder')}
                  maxLength={25}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {formData.displayName.length}/25
                </p>
              </div>
            ) : (
              <div className={`flex items-center ${isMobile ? 'flex-col space-y-2' : 'space-x-2'} w-full`}>
                <div className="flex-1 p-2 bg-muted rounded-md w-full">
                  {formData.displayName || t('no_display_name')}
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsChangingDisplayName(true)}
                  className={isMobile ? 'w-full' : ''}
                >
                  {t('change')}
                </Button>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="username">
              {t('username')} <span className="text-muted-foreground text-xs">(max 25 characters)</span>
            </Label>
            {isChangingUsername ? (
              <div className="space-y-2">
                <Input 
                  id="username" 
                  value={formData.username} 
                  onChange={e => setFormData({ ...formData, username: e.target.value.slice(0, 25) })} 
                  placeholder={t('username')}
                  maxLength={25}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {formData.username.length}/25
                </p>
                {!formData.usernameUpdateAllowed && (
                  <p className="text-sm text-destructive">
                    {t('username_cannot_change')} {formData.daysUntilUsernameChange} {t('more_days')}
                  </p>
                )}
              </div>
            ) : (
              <div className={`flex items-center ${isMobile ? 'flex-col space-y-2' : 'space-x-2'} w-full`}>
                <div className="flex-1 p-2 bg-muted rounded-md w-full">
                  @{formData.username || t('no_username')}
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsChangingUsername(true)}
                  disabled={!formData.usernameUpdateAllowed}
                  className={isMobile ? 'w-full' : ''}
                >
                  {t('change')}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{t('phone_number')}</Label>
            <Input 
              id="phone" 
              type="tel" 
              value={formData.phoneNumber} 
              onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
              placeholder={t('phone_number')} 
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('email')}</Label>
            <Input 
              id="email" 
              type="email" 
              value={formData.email} 
              disabled 
              className="bg-muted" 
            />
            <p className="text-sm text-muted-foreground">
              {t('email_cannot_change')}
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-left">{t('profile_links')}</h3>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="link-one">{t('link_one')}</Label>
                <Input 
                  id="link-one" 
                  type="url" 
                  value={formData.linkOne} 
                  onChange={e => setFormData({ ...formData, linkOne: e.target.value })}
                  placeholder="https://" 
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="link-two">{t('link_two')}</Label>
                <Input 
                  id="link-two" 
                  type="url" 
                  value={formData.linkTwo} 
                  onChange={e => setFormData({ ...formData, linkTwo: e.target.value })}
                  placeholder="https://" 
                />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={isLoadingProfile} className={isMobile ? 'w-full' : ''}>
            {isLoadingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('save_changes')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
